import { Box, Collapse, Stack, Typography, Paper } from "@mui/material";
import { Print, Receipt, AccessTime, LocalOffer, Description } from "@mui/icons-material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import styled from "@emotion/styled";
import { css } from "@mui/material";
import { useEffect, useState } from "react";

interface BotoesAcoesCheckoutProps {
    // Imprimir automático
    switchImprimirAutomatico: boolean;
    handleSwitchImprimirAutomatico: () => void;

    // Nota fiscal
    switchNotaFiscal: boolean;
    handleSwitchNotaFiscal: () => void;
    configuracaoNFCeObrigatorio?: boolean;

    // Horário (opcional - para edição pode não ter)
    agendadoPara?: moment.Moment | null;
    agendarHorario?: (hora: moment.Moment | null) => void;
    mostrarHorario?: boolean;
    toggleHorario?: () => void;

    // Cupons
    quantidadeCupons: number;
    abrirDrawerCupons: () => void;

    // Comandas (opcional - apenas para edição)
    abrirModalComandas?: () => void;
}

const ActionButtonContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1 1 0;
    min-width: 0;
    cursor: pointer;
`;

const IconCircleButton = styled(Paper, {
    shouldForwardProp: (prop) => prop !== "ativo",
})<{ ativo?: boolean }>(({ theme, ativo }) => {
    const baseColor = theme.palette.primary.main;

    return css`
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        box-shadow: ${theme.shadows[1]};
        background-color: ${ativo ? baseColor : theme.palette.background.paper};
        color: ${ativo ? theme.palette.common.white : theme.palette.text.secondary};
        cursor: pointer;
        transition: all 0.2s;

        svg {
            width: 18px;
            height: 18px;
        }

        &:hover {
            box-shadow: ${theme.shadows[2]};
            transform: translateY(-2px);
        }
    `;
});

const pickerSx = {
    width: "100%",
    '& .MuiFilledInput-root': {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: 1,
        minHeight: '56px',
        paddingTop: '0px !important',
        paddingBottom: '0px !important',
        '&:hover': { boxShadow: 2 },
        '&.Mui-focused': { boxShadow: 3 },
        '&::before': { display: 'none' },
        '&::after': { display: 'none' },
    },
    '& .MuiFilledInput-underline': {
        '&::before': { display: 'none' },
        '&::after': { display: 'none' },
    },
    '& .MuiInputBase-root': { display: 'flex', alignItems: 'center' },
    '& .MuiInputBase-input': { textAlign: 'left' },
    '& .MuiFilledInput-input': {
        padding: '18px 12px',
        paddingTop: '18px',
        paddingBottom: '18px',
        lineHeight: '1.5',
        display: 'flex',
        alignItems: 'center',
    },
    '& .MuiInputAdornment-root': {
        top: '50%',
        transform: 'translateY(-50%)',
        right: '12px',
    },
};

export const BotoesAcoesCheckout = ({
    switchImprimirAutomatico,
    handleSwitchImprimirAutomatico,
    switchNotaFiscal,
    handleSwitchNotaFiscal,
    configuracaoNFCeObrigatorio = false,
    agendadoPara,
    agendarHorario,
    mostrarHorario,
    toggleHorario,
    quantidadeCupons,
    abrirDrawerCupons,
    abrirModalComandas,
}: BotoesAcoesCheckoutProps) => {
    const [dataAgendamento, setDataAgendamento] = useState<moment.Moment>(moment());

    useEffect(() => {
        if (mostrarHorario) {
            // Ao abrir o painel, inicializar a data com a data do agendamento existente ou hoje
            setDataAgendamento(agendadoPara ? moment(agendadoPara) : moment());
        }
    }, [mostrarHorario]);

    const handleDataChange = (data: moment.Moment | null) => {
        if (!data) return;
        setDataAgendamento(data);
        if (agendadoPara && agendarHorario) {
            const combinado = data.clone().set({ hour: agendadoPara.hour(), minute: agendadoPara.minute() });
            agendarHorario(combinado);
        }
    };

    const handleHorarioChange = (hora: moment.Moment | null) => {
        if (!hora || !agendarHorario) return;
        const combinado = dataAgendamento.clone().set({ hour: hora.hour(), minute: hora.minute() });
        agendarHorario(combinado);
    };

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", marginTop: "1rem" }}>
            <Typography
                sx={{
                    fontSize: 11,
                    color: 'grey.500',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '12px',
                    fontFamily: 'Poppins, sans-serif'
                }}
            >
                AÇÕES RÁPIDAS
            </Typography>
            <Stack 
                direction="row" 
                spacing={1.5} 
                sx={{ 
                    width: "100%", 
                    maxWidth: "100%",
                    boxSizing: "border-box",
                }}
            >
                <ActionButtonContainer onClick={handleSwitchImprimirAutomatico}>
                    <IconCircleButton
                        ativo={switchImprimirAutomatico}
                        elevation={switchImprimirAutomatico ? 0 : 1}
                        role="button"
                    >
                        <Print />
                    </IconCircleButton>
                    <Typography
                        sx={{
                            fontSize: 10,
                            marginTop: '8px',
                            fontWeight: 500,
                            color: switchImprimirAutomatico ? 'primary.main' : 'text.secondary',
                            fontFamily: 'Poppins, sans-serif'
                        }}
                    >
                        IMPRIMIR
                    </Typography>
                </ActionButtonContainer>
                {!configuracaoNFCeObrigatorio && (
                    <ActionButtonContainer onClick={handleSwitchNotaFiscal}>
                        <IconCircleButton
                            ativo={switchNotaFiscal}
                            elevation={switchNotaFiscal ? 0 : 1}
                            role="button"
                        >
                            <Receipt />
                        </IconCircleButton>
                        <Typography
                            sx={{
                                fontSize: 10,
                                marginTop: '8px',
                                fontWeight: 500,
                                color: switchNotaFiscal ? 'primary.main' : 'text.secondary',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                        >
                            NOTA
                        </Typography>
                    </ActionButtonContainer>
                )}
                {toggleHorario && agendarHorario && (
                    <ActionButtonContainer onClick={toggleHorario}>
                        <IconCircleButton
                            ativo={mostrarHorario || false}
                            elevation={(mostrarHorario || false) ? 0 : 1}
                            role="button"
                        >
                            <AccessTime />
                        </IconCircleButton>
                        <Typography
                            sx={{
                                fontSize: 10,
                                marginTop: '8px',
                                fontWeight: 500,
                                color: (mostrarHorario || false) ? 'primary.main' : 'text.secondary',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                        >
                            AGENDAR
                        </Typography>
                    </ActionButtonContainer>
                )}
                <ActionButtonContainer onClick={abrirDrawerCupons}>
                    <IconCircleButton
                        ativo={quantidadeCupons > 0}
                        elevation={quantidadeCupons > 0 ? 0 : 1}
                        role="button"
                    >
                        <LocalOffer />
                    </IconCircleButton>
                    <Typography
                        sx={{
                            fontSize: 10,
                            marginTop: '8px',
                            fontWeight: 500,
                            color: quantidadeCupons > 0 ? 'primary.main' : 'text.secondary',
                            fontFamily: 'Poppins, sans-serif'
                        }}
                    >
                        CUPOM
                    </Typography>
                </ActionButtonContainer>
                {abrirModalComandas && (
                    <ActionButtonContainer onClick={abrirModalComandas}>
                        <IconCircleButton
                            ativo={false}
                            elevation={1}
                            role="button"
                        >
                            <Description />
                        </IconCircleButton>
                        <Typography
                            sx={{
                                fontSize: 10,
                                marginTop: '8px',
                                fontWeight: 500,
                                color: 'text.secondary',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                        >
                            COMANDAS
                        </Typography>
                    </ActionButtonContainer>
                )}
            </Stack>
            {mostrarHorario && agendarHorario && (
                <Collapse in={mostrarHorario}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <DatePicker
                            sx={pickerSx}
                            slotProps={{ textField: { variant: 'filled' } }}
                            format="DD/MM/YYYY"
                            label="Data"
                            value={dataAgendamento}
                            onChange={handleDataChange}
                        />
                        <TimePicker
                            sx={pickerSx}
                            slotProps={{ textField: { variant: 'filled' } }}
                            views={["hours", "minutes"]}
                            format="HH:mm"
                            label="Horário"
                            value={agendadoPara || null}
                            onChange={handleHorarioChange}
                        />
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};

